import React, { useEffect, useMemo, useState, useRef } from 'react'
import axios from 'axios'
import MakeupRequestModal from '../../modules/makeup/MakeupRequestModal'
import { KTSVG } from '../../../_metronic/helpers'
import { useAuth } from '../../modules/auth/core/Auth'

// 1) YYYY-MM-DD üretimi: ofset çıkarma yok, yerel takvimden oku
const formatDateKey = (date: Date) => {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
}

const API_URL = process.env.REACT_APP_API_URL
const GET_SCHEDULE_URL = `${API_URL}/Custom/GetInstructorSchedule`
const PRINT = { WIDTH: 1122, HEIGHT: 794, MARGIN: 40 } // A4 landscape @96dpi

// 2) Sunucudan gelen tarihleri yerel güne sabitle
// (ISO "YYYY-MM-DDTHH:mm:ss" veya "YYYY-MM-DD" fark etmez)
const parseLocalDate = (str: string) => {
    const dt = new Date(str) // utanmadan parse et
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()) // yerel güne pinle
}

interface ScheduleItem {
    id: number
    courseId?: number
    day: number
    startTime: string
    endTime: string
    courseName: string
    courseCode?: string
    classroomName: string
    departmentName?: string
    name?: string
    detail?: string
    isMakeup?: boolean
    date?: string
}

const InstructorWeeklySchedule: React.FC = () => {
    const { currentUser } = useAuth()
    const [schedule, setSchedule] = useState<ScheduleItem[]>([])
    const [semesterStart, setSemesterStart] = useState<Date | null>(null)
    const [semesterEnd, setSemesterEnd] = useState<Date | null>(null)
    const [weekIndex, setWeekIndex] = useState(0)
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 576)
    const [showMakeup, setShowMakeup] = useState(false)
    const [selectedCourse, setSelectedCourse] = useState<number | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const [printScale, setPrintScale] = useState(1)
    const [isPrinting, setIsPrinting] = useState(false)

    const COLORS = [
        '#FFCDD2',
        '#F8BBD0',
        '#E1BEE7',
        '#D1C4E9',
        '#C5CAE9',
        '#BBDEFB',
        '#B3E5FC',
        '#B2EBF2',
        '#B2DFDB',
        '#C8E6C9',
        '#DCEDC8',
        '#F0F4C3',
        '#FFF9C4',
        '#FFECB3',
        '#FFE0B2',
        '#FFCCBC',
        '#D7CCC8',
        '#F5F5F5',
        '#CFD8DC',
    ]

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 576)
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
        const afterPrint = () => {
            setIsPrinting(false)
            setPrintScale(1)
        }
        window.addEventListener('afterprint', afterPrint)
        return () => window.removeEventListener('afterprint', afterPrint)
    }, [])

    useEffect(() => {
        axios
            .get(GET_SCHEDULE_URL)
            .then((res) => {
                setSchedule(res.data.items || [])
                setSemesterStart(res.data.semesterStart ? parseLocalDate(res.data.semesterStart) : null)
                setSemesterEnd(res.data.semesterEnd ? parseLocalDate(res.data.semesterEnd) : null)
            })
            .catch(console.error)
    }, [])

    const days = useMemo(() => {
        const arr: Date[] = []
        if (semesterStart && semesterEnd) {
            const d = new Date(semesterStart)
            while (d <= semesterEnd) {
                arr.push(new Date(d))
                d.setDate(d.getDate() + 1)
            }
        }
        return arr
    }, [semesterStart, semesterEnd])

    const weeks = useMemo(() => {
        const result: Date[][] = []
        let week: Date[] = []
        days.forEach((date, idx) => {
            week.push(date)
            const isLast = idx === days.length - 1
            if (date.getDay() === 0 || isLast) {
                result.push(week)
                week = []
            }
        })
        return result
    }, [days])

    const itemsByDate = useMemo(() => {
        const map: Record<string, ScheduleItem[]> = {}
        days.forEach((date) => {
            const key = formatDateKey(date)
            map[key] = []
            const dow = date.getDay() === 0 ? 7 : date.getDay()
            schedule.forEach((item) => {
                if (item.isMakeup && item.date) {
                    const dkey = formatDateKey(new Date(item.date))
                    if (dkey === key) map[key].push(item)
                } else if (item.day === dow) {
                    map[key].push({ ...item })
                }
            })
        })
        return map
    }, [days, schedule])

    const currentWeek = weeks[weekIndex] || []

    const weekDays = useMemo(() => {
        return currentWeek.filter((date) => {
            const key = formatDateKey(date)
            const items = itemsByDate[key] || []
            const dow = date.getDay()
            if (items.length === 0 && (dow === 0 || dow === 6)) {
                return false
            }
            return true
        })
    }, [currentWeek, itemsByDate])

    const courseColors = useMemo(() => {
        const map = new Map<string, string>()
        let i = 0
        schedule.forEach((item) => {
            const key = item.courseCode || item.courseName
            if (!map.has(key)) {
                map.set(key, COLORS[i % COLORS.length])
                i++
            }
        })
        return map
    }, [schedule])

    const fullName = useMemo(() => {
        if (!currentUser) return ''
        const parts = [currentUser.title, currentUser.fullName].filter(Boolean)
        return parts.join(' ')
    }, [currentUser])

    const handlePrint = () => {
        const el = containerRef.current
        if (!el) {
            window.print()
            return
        }

        // İçeriğin gerçek ölçüsünü al
        const rect = el.getBoundingClientRect()
        const printableW = PRINT.WIDTH - PRINT.MARGIN * 2
        const printableH = PRINT.HEIGHT - PRINT.MARGIN * 2

        const scale = Math.min(printableW / rect.width, printableH / rect.height, 1)

        setPrintScale(scale)
        setIsPrinting(true)

        // Ölçek stilinin uygulanması ve reflow için 2 frame bekle
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                window.print()
            })
        })
    }

    return (
        <div
            ref={containerRef}
            className='container mt-4 schedule-print'
            style={{
                transform: !isPrinting && printScale !== 1 ? `scale(${printScale})` : undefined,
                transformOrigin: 'top left',
                width: isPrinting ? `${PRINT.WIDTH - PRINT.MARGIN * 2}px` : undefined,
                height: isPrinting ? `${PRINT.HEIGHT - PRINT.MARGIN * 2}px` : undefined,
                overflow: isPrinting ? 'hidden' : undefined,
                ['--print-scale' as any]: String(isPrinting ? printScale : 1),
            }}
        >
            <style>
                {`
  :root {
    --print-scale: 1;
  }
  @media print {
    @page { size: A4 landscape; margin: 10mm; }
    html, body { width: 297mm; height: 210mm; margin: 0; font-size: 12px; }
    /* Yazdırma sırasında aynı ölçeği uygula */
    .schedule-print {
      font-size: 12px;
      transform: scale(var(--print-scale));
      transform-origin: top left;
      width: ${PRINT.WIDTH - PRINT.MARGIN * 2}px !important;
      height: ${PRINT.HEIGHT - PRINT.MARGIN * 2}px !important;
      overflow: hidden !important;
      margin: 0 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .schedule-print * { font-size: inherit !important; }
    .makeup-item, .makeup-group, .noprint { display: none !important; }
  }
`}
            </style>
            <h2 className='mb-4'>{fullName ? `${fullName} haftalık programı` : 'Haftalık Programı'}</h2>
            <div className='alert alert-primary noprint'>Değerli öğretim elemanları, ortak derslerde dersin sorumlusu olarak dersin koordinatörü görünmektedir.
                OBS sisteminde dersin not sorumlusu kim ise derslik sisteminde de onun adı çıkmaktadır. Bu durumdaki öğretim elemanlarının normal şartlarda herhangi bir işlem yapması gerekmemektedir.
                Ancak programda dersi fiili olarak veren öğretim elemanının görünmesi gerektiği durumlarda öğrenci işleri ile irtibata geçerek durumu iletebilirsiniz.</div>
            <div className='d-flex justify-content-between align-items-center mb-3 noprint'>
                <button
                    className='btn btn-light btn-sm'
                    disabled={weekIndex === 0}
                    onClick={() => setWeekIndex((w) => w - 1)}
                >
                    Önceki Hafta
                </button>
                <span className='fw-bold'>{weekIndex + 1}. Hafta</span>
                <div>
                    <button
                        className='btn btn-light btn-sm me-2'
                        disabled={weekIndex >= weeks.length - 1}
                        onClick={() => setWeekIndex((w) => w + 1)}
                    >
                        Sonraki Hafta
                    </button>
                    <button className='btn btn-primary btn-sm' onClick={handlePrint}>
                        Yazdır
                    </button>
                </div>
            </div>
            <div className='d-flex flex-column flex-sm-row'>
                {weekDays.map((date) => {
                    const key = formatDateKey(date)
                    const items = itemsByDate[key] || []

                    const courseGroups = Array.from(
                        items.reduce((acc, item) => {
                            const ck = item.courseCode || item.courseName
                            if (!acc.has(ck)) acc.set(ck, [] as ScheduleItem[])
                            acc.get(ck)!.push(item)
                            return acc
                        }, new Map<string, ScheduleItem[]>())
                    )

                    const dayWidth = isMobile ? '100%' : `${100 / weekDays.length}%`
                    return (
                        <div
                            key={key}
                            className='border border-2 rounded me-sm-2 mb-2 mb-sm-0 flex-shrink-0'
                            style={{ width: dayWidth }}
                        >
                            <div className='bg-light text-center fw-bold p-2'>
                                {isPrinting
                                    ? date.toLocaleDateString('tr-TR', { weekday: 'long' })
                                    : date.toLocaleDateString('tr-TR', {
                                        weekday: 'short',
                                        day: 'numeric',
                                        month: 'numeric',
                                    })}
                            </div>
                            {courseGroups.length > 0 ? (
                                courseGroups.map(([ck, items]) => {
                                    const color = courseColors.get(ck) || '#ccc'
                                    const isMakeup = items.some((i) => i.isMakeup)
                                    const allMakeup = items.every((i) => i.isMakeup)
                                    return (
                                        <div key={ck} className={`p-2 border-top ${allMakeup ? 'makeup-group' : ''}`}>
                                            <div
                                                className='p-2 rounded border'
                                                style={{ borderColor: color, backgroundColor: `${color}33` }}
                                            >
                                                <div className='fw-semibold'>
                                                    {items[0].courseCode
                                                        ? `${items[0].courseName} (${items[0].courseCode})`
                                                        : items[0].courseName}
                                                    {isMakeup && (
                                                        <span className='badge bg-warning text-dark ms-1 noprint'>Telafi</span>
                                                    )}
                                                </div>
                                                {items.map((g, idx) => (
                                                    <div
                                                        key={idx}
                                                        className={`text-muted small position-relative pe-4 pb-4 ${g.isMakeup ? 'makeup-item' : ''
                                                            }`}
                                                    >
                                                        {g.startTime} - {g.endTime} {g.classroomName}
                                                        {g.departmentName && ` - ${g.departmentName}`}
                                                        {g.name && ` - ${g.name.split(' - ').pop()}`}
                                                        {!g.isMakeup && g.courseId && (
                                                            <button
                                                                className='btn btn-icon btn-sm btn-light-primary position-absolute bottom-0 end-0 m-1 noprint'
                                                                title='Telafi Talebi'
                                                                onClick={() => {
                                                                    setSelectedCourse(g.courseId as number)
                                                                    setShowMakeup(true)
                                                                }}
                                                            >
                                                                <KTSVG
                                                                    path='/media/icons/duotune/arrows/arr029.svg'
                                                                    className='svg-icon-3'
                                                                />
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )
                                })
                            ) : (
                                <div className='p-2 text-muted'>Ders yok</div>
                            )}
                        </div>
                    )
                })}
            </div>
            <MakeupRequestModal
                show={showMakeup}
                courseId={selectedCourse}
                onClose={() => setShowMakeup(false)}
            />
        </div>
    )
}

export default InstructorWeeklySchedule
